import { useState } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { Tooltip } from "@monolith-forensics/monolith-ui";

import {
  CopyableRow,
  getLatestRecord,
  LatestCocSection,
  TooltipBody,
} from "@/components/CocTooltip";
import { CocQuery } from "@/Features/ChainOfCustody/coc.query";
import { useCaseContext } from "@/Features";
import { evidenceDetailPath } from "@/Features/Evidence/evidenceLinks";
import { EVIDENCE_TAB_SLUGS } from "@/pages/Evidence/[uuid]/views/evidenceTabSlugs";

interface LocationCellProps {
  rowData: {
    evidence_id: number | string;
    uuid: string;
    location_name: string;
    location_group_name?: string;
    office_name?: string;
  };
}

const ViewCocLink = styled(Link)`
  color: ${({ theme }) => theme.palette.primary.main};
  font-size: 12px;
  &:hover {
    text-decoration: underline;
  }
`;

const LocationCell: React.FC<LocationCellProps> = ({ rowData }) => {
  const [hovered, setHovered] = useState(false);
  const caseContext = useCaseContext();
  // isPending, not isLoading: isLoading is false while the query is disabled
  // (pre-hover) with no data yet, which would flash the definitive "No chain
  // of custody records" line. retry: false — the tooltip already shows the
  // failure, and each retried request fires another interceptor error toast.
  const { data, isPending, isError } = CocQuery.useGetCocRecords(
    rowData.evidence_id,
    {
      enabled: hovered,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: false,
    }
  );

  if (!rowData.location_name) return null;

  const content = (
    <TooltipBody>
      <div className="tooltip-title">Location info</div>
      <CopyableRow label="Current Location" value={rowData.location_name} />
      <CopyableRow label="Location Group" value={rowData.location_group_name} />
      <CopyableRow label="Office Location" value={rowData.office_name} />
      <LatestCocSection
        latest={getLatestRecord(data)}
        isPending={isPending}
        isError={isError}
        footer={
          <ViewCocLink
            to={evidenceDetailPath(rowData.uuid, {
              tab: EVIDENCE_TAB_SLUGS.COC,
              caseId: caseContext?.data?.case_id,
            })}
          >
            View full chain of custody
          </ViewCocLink>
        }
      />
    </TooltipBody>
  );

  return (
    <Tooltip
      content={content}
      hoverableContent
      delayDuration={400}
      side="bottom"
      align="start"
      sideOffset={5}
    >
      <span
        tabIndex={0}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onFocus={() => setHovered(true)}
        onBlur={() => setHovered(false)}
      >
        {rowData.location_name}
      </span>
    </Tooltip>
  );
};

export default LocationCell;
